/*
 * Events and their rooms. Organiser tier.
 *
 * "Make live" here is the real thing: the app reads `event.is_current` at startup, so
 * changing it moves every screen for everybody on their next load, with no rebuild. It
 * asks for confirmation for that reason.
 *
 * Rooms can be added and renamed, never deleted: `slot.id_venue` and `qr_venue` both
 * point at them with no foreign key behind either, so a delete leaves sessions and
 * access grants pointing at nothing, and no screen would show you either.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { CalendarDays, Check, DoorOpen, Pencil, Plus, X } from 'lucide-react'
import { formatDateRange } from '../config/event'
import { api, ID_EVENT } from '../lib/api'
import type { AdminEventsResponse } from '../lib/types'
import { useAuthed } from '../lib/session'
import { Masthead } from '../ui/Chrome'
import { Button } from '../ui/Button'
import { Field } from '../ui/Field'
import { Notice, Skeleton } from '../ui/Feedback'

const BLANK = {
  id: null as number | null, event_name: '', short_name: '', organizer: '',
  venue: '', event_start: '', event_end: '', status: 1,
}

export function EventAdminRoute() {
  const session = useAuthed()
  const [data, setData] = useState<AdminEventsResponse | null>(null)
  const [failed, setFailed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /* Which event's rooms are open. Defaults to the one this build serves. */
  const [openEvent, setOpenEvent] = useState<number | null>(ID_EVENT)
  const [draft, setDraft] = useState<typeof BLANK | null>(null)
  const [newRoom, setNewRoom] = useState('')
  const [renaming, setRenaming] = useState<{ id: number; name: string } | null>(null)

  const load = useCallback(() => {
    setFailed(false)
    api.adminEvents(session.email, session.at).then(setData).catch(() => setFailed(true))
  }, [session.email, session.at])

  useEffect(load, [load])

  const roomsFor = useMemo(() => {
    const map = new Map<number, AdminEventsResponse['venues']>()
    for (const v of data?.venues ?? []) {
      if (v.id_event === null) continue
      const list = map.get(v.id_event) ?? []
      list.push(v)
      map.set(v.id_event, list)
    }
    return map
  }, [data])

  async function run(work: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await work()
      load()
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'That did not save.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="page eventadmin-page">
      <Masthead kicker="Organiser" title="Events and rooms" />

      {data === null && !failed ? <Skeleton height="16rem" /> : null}

      {failed ? (
        <Notice
          icon={<CalendarDays size={22} strokeWidth={1.3} />}
          title="Could not load events"
          body="The server did not answer, or this account is not an organiser."
          action={{ label: 'Try again', onClick: load }}
        />
      ) : null}

      {data ? (
        <>
          <div className="eventadmin-actions">
            <Button
              variant="primary"
              size="sm"
              icon={<Plus size={16} strokeWidth={1.7} />}
              onClick={() => setDraft({ ...BLANK })}
            >
              New event
            </Button>
          </div>

          {error ? <p className="field__error eventadmin-error" role="alert">{error}</p> : null}

          {draft ? (
            <EventForm
              draft={draft}
              busy={busy}
              onChange={setDraft}
              onCancel={() => setDraft(null)}
              onSave={() => void run(async () => {
                await api.adminSaveEvent(session.email, session.at, {
                  id: draft.id,
                  event_name: draft.event_name.trim(),
                  short_name: draft.short_name.trim(),
                  organizer: draft.organizer.trim(),
                  venue: draft.venue.trim(),
                  event_start: draft.event_start,
                  event_end: draft.event_end,
                  status: draft.status,
                })
                setDraft(null)
              })}
            />
          ) : null}

          {data.events.map((event) => {
            const live = event.id === data.live_id_event
            const rooms = roomsFor.get(event.id) ?? []
            const open = openEvent === event.id
            return (
              <article className="eventadmin-card" key={event.id} data-live={live || undefined}>
                {/*
                  * One line, one job. This used to be five lines of near-equal weight —
                  * name, an ISO date range with the id trailing it, venue, a run-on of
                  * four counts, then the buttons — plus two badges that said the same
                  * thing. Now: the name leads, the dates and venue step back, the counts
                  * are a figure with a label under it, and the id sits with the actions
                  * where you go looking for it.
                  */}
                {/*
                  * The badge sits inside the title, not in a column beside it: these
                  * names run to two lines at 375px, and a pill pinned to the top right
                  * of a wrapped title reads as belonging to nothing. Inline, it follows
                  * the last word.
                  */}
                <p className="eventadmin-card__name">
                  {event.event_name}{' '}
                  {/* `event.is_current`. Exactly one, enforced by a filtered index. */}
                  {live ? <span className="eventadmin-live">Live now</span> : null}
                  {/*
                    * Only the exception is worth a badge. "Active" beside "Live now" was
                    * two labels for one fact, and active-but-not-live is the ordinary
                    * state of most of this list — the card being unremarkable says it.
                    */}
                  {event.status === 0 ? <span className="eventadmin-archived">Archived</span> : null}
                </p>

                <p className="eventadmin-card__when tnum">
                  {formatDateRange(event.event_start, event.event_end)}
                </p>
                {event.venue ? <p className="eventadmin-card__where">{event.venue}</p> : null}

                {/*
                  * dt before dd so a screen reader still reads "sessions, 45"; CSS turns
                  * the column round so the eye gets the figure first. The label also
                  * settles the plural — "1 participants" was in the old run-on.
                  */}
                <dl className="eventadmin-stats">
                  {([
                    ['Sessions', event.counts.slots],
                    ['Tracks', event.counts.tracks],
                    ['Rooms', event.counts.venues],
                    ['People', event.counts.participants],
                  ] as const).map(([label, value]) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd className="tnum">{value}</dd>
                    </div>
                  ))}
                </dl>

                <div className="eventadmin-card__foot">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<Pencil size={15} strokeWidth={1.6} />}
                    onClick={() => setDraft({
                      id: event.id,
                      event_name: event.event_name,
                      short_name: event.short_name ?? '',
                      organizer: event.organizer ?? '',
                      venue: event.venue ?? '',
                      event_start: event.event_start,
                      event_end: event.event_end,
                      status: event.status,
                    })}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="quiet"
                    size="sm"
                    icon={<DoorOpen size={15} strokeWidth={1.6} />}
                    onClick={() => setOpenEvent(open ? null : event.id)}
                  >
                    {open ? 'Hide rooms' : `Rooms (${rooms.length})`}
                  </Button>
                  {/*
                    * Changes what every delegate sees on their next load, so it asks
                    * once. Archived events are refused by the server; not offering the
                    * control is the same answer given earlier.
                    */}
                  {!live && event.status === 1 ? (
                    <Button
                      variant="quiet"
                      size="sm"
                      busy={busy}
                      onClick={() => {
                        if (!window.confirm(
                          `Make "${event.event_name}" the live event?

` +
                          'Everyone using the app will see this event instead, the next time they open it.',
                        )) return
                        void run(() => api.adminSetCurrentEvent(session.email, session.at, event.id))
                      }}
                    >
                      Make live
                    </Button>
                  ) : null}
                  {/* Kept because support asks for it, demoted because nobody reads it
                      until they do. */}
                  <span className="eventadmin-card__id tnum">id {event.id}</span>
                </div>

                {open ? (
                  <div className="eventadmin-rooms">
                    {rooms.length === 0 ? (
                      <p className="eventadmin-card__meta">No rooms yet.</p>
                    ) : (
                      <ul className="eventadmin-roomlist">
                        {rooms.map((room) => (
                          <li key={room.id}>
                            {renaming?.id === room.id ? (
                              <div className="eventadmin-rename">
                                <input
                                  className="field__input"
                                  value={renaming.name}
                                  onChange={(e) => setRenaming({ id: room.id, name: e.target.value })}
                                  aria-label={`Rename room ${room.id}`}
                                />
                                <button
                                  type="button"
                                  className="icon-button"
                                  disabled={busy || !renaming.name.trim()}
                                  aria-label="Save name"
                                  onClick={() => void run(async () => {
                                    await api.adminSaveVenue(session.email, session.at, {
                                      id: room.id, id_event: room.id_event, venue_name: renaming.name.trim(),
                                    })
                                    setRenaming(null)
                                  })}
                                >
                                  <Check size={17} strokeWidth={2} />
                                </button>
                                <button
                                  type="button"
                                  className="icon-button"
                                  aria-label="Cancel"
                                  onClick={() => setRenaming(null)}
                                >
                                  <X size={17} strokeWidth={1.8} />
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="eventadmin-room"
                                onClick={() => setRenaming({ id: room.id, name: room.venue_name ?? '' })}
                              >
                                {/* A nameless row is the reason this screen exists — venue 12. */}
                                <span data-unnamed={!room.venue_name || undefined}>
                                  {room.venue_name || `Unnamed (id ${room.id})`}
                                </span>
                                <Pencil size={13} strokeWidth={1.7} />
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="eventadmin-rename">
                      <input
                        className="field__input"
                        value={newRoom}
                        placeholder="Add a room"
                        aria-label={`Add a room to ${event.event_name}`}
                        onChange={(e) => setNewRoom(e.target.value)}
                      />
                      <button
                        type="button"
                        className="icon-button"
                        disabled={busy || !newRoom.trim()}
                        aria-label="Add room"
                        onClick={() => void run(async () => {
                          await api.adminSaveVenue(session.email, session.at, {
                            id: null, id_event: event.id, venue_name: newRoom.trim(),
                          })
                          setNewRoom('')
                        })}
                      >
                        <Plus size={17} strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                ) : null}
              </article>
            )
          })}

          <p className="eventadmin-note">
            The live event is whichever one is marked here — the app reads it at startup
            from <code>event.is_current</code>, along with the name, venue, dates and the
            certificate threshold. Changing it takes effect for everyone the next time
            they open the app; no rebuild. There is no built-in copy to fall back on — an
            app that cannot reach the server says so rather than showing an event it
            guessed at.
          </p>
        </>
      ) : null}
    </main>
  )
}

function EventForm({
  draft, busy, onChange, onCancel, onSave,
}: {
  draft: typeof BLANK
  busy: boolean
  onChange: (next: typeof BLANK) => void
  onCancel: () => void
  onSave: () => void
}) {
  const complete = draft.event_name.trim() && draft.event_start && draft.event_end
  return (
    <section className="eventadmin-form">
      <h2 className="eventadmin-form__title">{draft.id ? 'Edit event' : 'New event'}</h2>

      <Field
        label="Name"
        value={draft.event_name}
        onChange={(e) => onChange({ ...draft, event_name: e.target.value })}
        placeholder="Continuing Professional Development 2027"
      />
      <div className="eventadmin-dates">
        <Field
          label="Short name"
          value={draft.short_name}
          onChange={(e) => onChange({ ...draft, short_name: e.target.value })}
          placeholder="CPD 2027"
          hint="Printed on the E-Pass."
        />
        <Field
          label="Organiser"
          value={draft.organizer}
          onChange={(e) => onChange({ ...draft, organizer: e.target.value })}
          placeholder="Universiti Teknologi MARA"
        />
      </div>
      <Field
        label="Venue"
        value={draft.venue}
        onChange={(e) => onChange({ ...draft, venue: e.target.value })}
        hint="The building, as printed on the programme. Rooms are managed per event below."
      />
      <div className="eventadmin-dates">
        <Field
          label="First day"
          type="date"
          value={draft.event_start}
          onChange={(e) => onChange({ ...draft, event_start: e.target.value })}
        />
        <Field
          label="Last day"
          type="date"
          value={draft.event_end}
          onChange={(e) => onChange({ ...draft, event_end: e.target.value })}
        />
      </div>

      <label className="useradmin-card__role">
        <span className="kicker">Status</span>
        <select value={draft.status} onChange={(e) => onChange({ ...draft, status: Number(e.target.value) })}>
          <option value={1}>Active</option>
          <option value={0}>Archived</option>
        </select>
      </label>

      <div className="eventadmin-form__foot">
        <Button variant="primary" size="sm" busy={busy} disabled={!complete} onClick={onSave}>
          {draft.id ? 'Save changes' : 'Create event'}
        </Button>
        <Button variant="quiet" size="sm" onClick={onCancel}>Cancel</Button>
      </div>
    </section>
  )
}
